import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import { addDays, format, isAfter, isBefore } from 'date-fns';
import { Alert, Platform } from 'react-native';
import { EventRegister } from 'react-native-event-listeners';
import * as RNIap from 'react-native-iap';
import {
  acknowledgePurchaseAndroid,
  clearTransactionIOS,
  finishTransaction,
  getAvailablePurchases,
  requestSubscription,
  setup,
  validateReceiptIos
} from 'react-native-iap';
import AuthenticationController from './AuthenticationController';
import DataController from './DataController';

const skus = Platform.select({
  ios: [
    'com.aimermedia.Lectionary.3months.auto.offline',
    'com.aimermedia.Lectionary.12months.auto.offline',
    'com.aimermedia.Lectionary.3months.auto.online',
    'com.aimermedia.Lectionary.12months.auto.online',
  ],
  android: [
    'com.aimer.lectionary.android.offline.1.month.v02',
    'com.aimer.lectionary.android.offline.12.months.v02',
    'com.aimer.lectionary.android.online.1.month.v02',
    'com.aimer.lectionary.android.online.12.months.v02',
  ],
});

const IAPController = {
  serverURL() {
    // return 'http://localhost:8013';
//     return 'https://cofe.aimernginx.co.uk';
    return 'https://hymns-api.hymnsam.co.uk';
  },

  async initConnection() {
    try {
      Platform.OS === 'ios' && setup({ storekitMode: 'STOREKIT1_MODE' });
      await RNIap.initConnection();
    } catch (error) {
      console.log(error);
    }
  },

  async endConnection() {
    try {
      await RNIap.endConnection();
    } catch (error) {
      console.log(error);
    }
  },

  async getSubscriptions() {
    try {
      const subscriptions = await RNIap.getSubscriptions({ skus });
      return subscriptions;
    } catch (error) {
      console.log(error);

      return [];
    }
  },

  async checkSubscriptionIsActive(latestPurchases) {
    try {
      let purchases = latestPurchases;

      if (!purchases) {
        purchases = await getAvailablePurchases();
      }

      const user = await AuthenticationController.getUser();

      if (purchases && purchases.length > 0) {
        if (Platform.OS === 'ios') {
          const sortedPurchases = purchases.sort((a, b) => b.transactionDate - a.transactionDate);
          const latestPurchase = sortedPurchases[0]
          const latestReceipt = latestPurchase.transactionReceipt;

          const body = {
            receipt: latestReceipt,
            bid: 'com.aimer.lectionary',
            // password: 'e224329dc512443888971cdfcebfacf0',
          };

          let bodyEncode = new URLSearchParams(body);

          const requestUrl = `${this.serverURL()}/lectionary/verifyWithApple`;

          try {
            const response = await fetch(requestUrl, {
              body: bodyEncode.toString(),
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              method: 'POST',
              redirect: 'follow',
            });

            let json;

            try {
              json = await response.json();
            } catch (error) {
              console.log(error);
            }

            if (json && json.appleResponse) {
              const expiryDate = json.appleResponse.expires_date_ms;
              const expiryDateMilliseconds = Number(expiryDate);

              if (latestPurchase) {
                await DataController.saveCurrentSubscription(json.appleResponse);
                await DataController.saveExpiryDate(expiryDate);
              }

              EventRegister.emit('subscriptions/subscription-updated');
            }
          } catch (error) {
            console.log('checkSubscriptionIsActive - ios', error);
            EventRegister.emit('subscriptions/subscription-updated');
          }
        } else if (Platform.OS === 'android') {
          let latestPurchase = purchases[purchases.length - 1];
          const purchaseToken = latestPurchase.purchaseToken;
          const productId = latestPurchase.productId;

          const requestUrl = `${this.serverURL()}/lectionary/verifyWithGoogle`;

          __DEV__ && console.log(requestUrl);

          let body = {
            purchaseToken: purchaseToken,
            bid: 'com.aimer.lectionary',
            subId: productId,
          };

          let formBody: any = [];

          for (let property in body) {
            let encodedKey = encodeURIComponent(property);
            let encodedValue = encodeURIComponent(body[property]);
            formBody.push(encodedKey + "=" + encodedValue);
          }

          formBody = formBody.join("&");

          try {
            const response = await fetch(requestUrl, {
              body: formBody,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              method: 'POST',
              redirect: 'follow',
            });

            let json;

            try {
              json = await response.json();
              console.log('json', json);
            } catch (error) {
              console.log(error);
            }

            if (json && json.googleResponse && json.message === 'Verified') {
              const expiryDate = json.googleResponse.payload.expiryTimeMillis;
              const expiryDateMilliseconds = Number(expiryDate);

              if (latestPurchase) {
                __DEV__ && console.log(latestPurchase);
                latestPurchase.expiryDate = expiryDateMilliseconds
                await DataController.saveCurrentSubscription(latestPurchase);
                await DataController.saveExpiryDate(expiryDate);
              }

              EventRegister.emit('subscriptions/subscription-updated');
            }

            const result = await acknowledgePurchaseAndroid({
              token: purchaseToken,
            });
          } catch (error) {
            console.log('checkSubscriptionIsActive - android', error);
            EventRegister.emit('subscriptions/subscription-updated');
          }
        }
      }
    } catch (error) {
      console.log('checkSubscriptionIsActive - general', error);
    }
  },

  async buySubscription(subscriptionRequest, user) {
    try {
      __DEV__ && console.log(subscriptionRequest);

      if (Platform.OS === 'ios') {
        await clearTransactionIOS();
      }

      const purchase = await requestSubscription(subscriptionRequest);

      __DEV__ && console.log('buySubscription', purchase);

      if (purchase) {
        await this.checkSubscriptionIsActive(purchase);
        __DEV__ && console.log('buySubscription = checkSubscriptionIsActive');

        if (Platform.OS === 'ios') {
          const result = await finishTransaction({
            purchase,
            isConsumable: false,
          });
          __DEV__ && console.log('buySubscription = finishTransaction', result);
        }

        user && await this.updateHymnsSubscription(user);
      }
    } catch (error) {
      console.log(error);
      Alert.alert('Purchase cancelled', 'Subscription purchase cancelled, you have not been charged');
    }
  },

  async updateHymnsSubscription(user) {
    try {
      const purchases = await getAvailablePurchases();

      if (user && purchases) {
        if (Platform.OS === 'ios') {
          const latestSubscription = await DataController.getCurrentSubscription();

          if (latestSubscription) {
            const data = {
              bid: latestSubscription.product_id,
              transactionID: latestSubscription.transaction_id,
              platform: 'iOS',
            };

            const token = user.token;
            const startDate = new Date(Number(latestSubscription.original_purchase_date_ms));
            const endDate = addDays(new Date(Number(latestSubscription.expires_date_ms)), 1);

            await this.updateHymnsAccountWithSubscription(token, startDate, endDate, data);
          }
        } else if (Platform.OS === 'android') {
          const latestSubscription = await DataController.getCurrentSubscription();
          const expiry = await DataController.getExpiryDate();

          if (latestSubscription) {
            const data = {
              bid: latestSubscription.productId,
              purchaseToken: latestSubscription.purchaseToken,
              platform: 'android',
            };

            const token = user.token;
            const startDate = new Date(Number(latestSubscription.transactionDate));
            const endDate = addDays(new Date(Number(expiry)), 1);

            await this.updateHymnsAccountWithSubscription(token, startDate, endDate, data);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  },

  async updateHymnsAccountWithSubscription(token, startDate, endDate, additional) {
    return new Promise(async (resolve, reject) => {
      try {
        const formattedStart = format(startDate, 'dd-MM-yyyy');
        const formattedEnd = format(endDate, 'dd-MM-yyyy');

        const url = `https://rest.hymnsam.co.uk/api/subscription?token=${token}&startdate=${formattedStart}&expirydate=${formattedEnd}&publicationid=21&createdbyid=1&additionalAppData=${JSON.stringify(additional)}`;
        let response = null;

        const body = {
          ...(token && { Token: token }),
        };

        /**
        * Example response
            {
              "ResponseMessage": "Success",
              "Subscriptions": [
                  {
                      "Title": "Lectionary",
                      "PublicationId": 21,
                      "SubscriptionCreatedById": 0,
                      "SubscriptionId": 253897,
                      "StartDate": "2020-09-17T00:00:00",
                      "ExpiryDate": "2025-09-17T00:00:00",
                      "Status": 1,
                      "AdditionalAppData": "NOSET"
                  }
              ]
          }

          {
            "ResponseMessage": "Success",
              "Subscriptions": []
          }
        */

        response = await fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });

        const responseJson = await response.json();

        __DEV__ && console.log('hmynsAPI', responseJson);

        if (responseJson.ResponseMessage === 'Success') {
          try {
            AsyncStorage.setItem('hymnsSubscription', JSON.stringify(responseJson.Subscriptions));
            EventRegister.emit('subscriptions/hymns-subscription-updated');
            resolve(responseJson.Subscriptions);
          } catch (error) {
            console.log('error saving subscription info: ' + error);
            reject(error);
          }
        } else {
          /*   {
              "ResponseMessage": "User not found",
                "Subscriptions": null
            } */
          reject(responseJson);
        }
      } catch (error) {
        console.log(error);
        reject('updateHymnsSubscription', error);
      }
    });
  },

  async restorePurchases() {
    try {
      EventRegister.emit('subscriptions/restoring-purchases', true);

      const purchases = await getAvailablePurchases();
      const user = await AuthenticationController.getUser();

      if (purchases.length > 0) {
        const latestPurchase = purchases[purchases.length - 1];

        try {
          await this.checkSubscriptionIsActive(purchases);
          user && await this.updateHymnsSubscription(user);

          EventRegister.emit('subscriptions/restoring-purchases', false);

          Alert.alert(
            'Restore Successful',
            'You successfully restored your purchase',
          );
        } catch (error) {
          EventRegister.emit('subscriptions/restoring-purchases', false);

          Alert.alert(
            'Error',
            'An error occurred while attempting to restore your purchases',
          );
        }
      } else {
        EventRegister.emit('subscriptions/restoring-purchases', false);
        Alert.alert('No purchases to restore');
      }
    } catch (error) {
      console.warn(error);
      EventRegister.emit('subscriptions/restoring-purchases', false);

      if (error !== null && error.code !== null && error.code !== 'E_USER_CANCELLED') {
        Alert.alert(error.message);
      }
    }
  },

  async checkHymnsSubscriptionIsValid(hymnsSub) {
    const endDate = new Date(hymnsSub[0].ExpiryDate);

    if (hymnsSub[0].AdditionalAppData !== 'NOSET') {
      const data = JSON.parse(hymnsSub[0].AdditionalAppData);
      let isOnlineSubscription = data.bid.includes('online');

      __DEV__ && console.log('isOnlineSubscription', isOnlineSubscription);

      if (isAfter(new Date(), endDate)) {
        __DEV__ && console.log('isAfter');
        return false;
      } else if (isOnlineSubscription) {
        const info = await NetInfo.fetch();
        const isConnected = info.isConnected;

        console.log('isConnected', isConnected);

        if (isConnected) {
          return true;
        } else {
          return false;
        }
      } else {
        return isBefore(new Date(), endDate);
      }
    } else {
      return isBefore(new Date(), endDate);
    }
  },

  getMoreRecentSubscription(hymnsSub, appSub, expiry) {
    const endDateHymns = new Date(hymnsSub[0].ExpiryDate);
    const endDateApp = Platform.OS === 'ios'
      ? new Date(Number(appSub.expires_date_ms))
      : new Date(Number(expiry));

    let latest;

    if (isAfter(endDateHymns, endDateApp)) {
      latest = { type: 'hymns', date: endDateHymns };
    } else {
      latest = { type: 'app', date: endDateApp };
    }

    return latest;
  },

  async checkAppSubscriptionIsValid(appSub, expiry) {
    __DEV__ && console.log('checkAppSubscriptionIsValid', appSub, expiry);
    const endDate = Platform.OS === 'ios'
      ? new Date(Number(appSub.expires_date_ms))
      : new Date(Number(expiry));

    let isOnlineSubscription = false;

    if (Platform.OS === 'ios') {
      isOnlineSubscription = appSub.product_id.includes('online');
    } else if (Platform.OS === 'android') {
      isOnlineSubscription = appSub.productId.includes('online');
    }

    if (isAfter(new Date, endDate)) {
      return false;
    } else if (isOnlineSubscription) {
      const info = await NetInfo.fetch();
      const isConnected = info.isConnected;

      if (isConnected) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  },

  checkTrialIsActive(trialEnd) {
    const trialActive = isBefore(new Date(), trialEnd);
    // __DEV__ && console.log(trialEnd, trialActive);
    return trialActive;
    // return false;
  },

};

export default IAPController;
